import { TCS_ISSUER } from './lib/billing';

interface MerchantIssuer {
  name: string;
  companyAddress?: string | null;
  logoUrl?: string | null;
}

interface Props {
  /** ONE_TIME invoices are merchant → customer; everything else is TCS → merchant. */
  isMerchantIssued: boolean;
  merchant?: MerchantIssuer | null;
}

/**
 * Document header for an invoice/receipt: issuer logo + business-details block (PE-2105).
 * TCS-issued documents (platform billing a merchant) use TCS's own fixed details. Merchant-
 * issued documents (a merchant billing their customer) use that merchant's own logo/name/
 * address as sourced from the merchant record + brand assets — never hardcoded.
 */
export function InvoiceHeader({ isMerchantIssued, merchant }: Props) {
  if (isMerchantIssued) {
    return (
      <div className="flex items-start gap-4 pb-6 border-b border-border">
        {merchant?.logoUrl ? (
          <img src={merchant.logoUrl} alt={`${merchant.name} logo`} className="h-12 w-12 rounded-lg object-cover border border-border" />
        ) : (
          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-xs font-semibold">
            {(merchant?.name ?? '?').slice(0, 2).toUpperCase()}
          </div>
        )}
        <div>
          <p className="text-base font-semibold text-foreground">{merchant?.name ?? 'Merchant'}</p>
          {merchant?.companyAddress && (
            <p className="text-xs text-muted-foreground whitespace-pre-wrap max-w-sm">{merchant.companyAddress}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4 pb-6 border-b border-border">
      <div className="h-12 w-12 rounded-lg bg-[#28282A] flex items-center justify-center text-white text-xs font-bold">
        TCS
      </div>
      <div>
        <p className="text-base font-semibold text-foreground">{TCS_ISSUER.name}</p>
        {TCS_ISSUER.addressLines.map((line) => (
          <p key={line} className="text-xs text-muted-foreground">{line}</p>
        ))}
        <p className="text-xs text-muted-foreground mt-1">
          {TCS_ISSUER.phone} · {TCS_ISSUER.email} · {TCS_ISSUER.website}
        </p>
      </div>
    </div>
  );
}
